import { computed, onMounted } from 'vue';
import { useMarketingSectionsStore } from '@/stores/marketing_sections';

/**
 * Loads a marketing placement and reports whether it has anything to render.
 *
 * A placement that resolves to nothing takes its whole column with it, so the page
 * needs the answer before it renders the wrapper.
 *
 * @param {string} slug placement slug
 * @param {Object} options
 * @param {import('vue').Ref<string[]>|string[]} options.exclude_types widget types this placement must not render
 */
export function useMarketingSection(slug, options = {}) {
	const marketingSectionsStore = useMarketingSectionsStore();

	const excluded = computed(() => {
		const value = options.exclude_types;

		if (!value) return [];

		return typeof value === 'object' && 'value' in value ? value.value : value;
	});

	const load = () => {
		const section = marketingSectionsStore.getSection(slug);

		if (section && !section.failed) return;

		marketingSectionsStore.fetchSection(slug).catch(() => {});
	};

	onMounted(load);

	const widgets = computed(() => marketingSectionsStore.getWidgets(slug, excluded.value));
	const has_content = computed(() => widgets.value.length > 0);

	// A caller with a fallback has to wait for the answer: rendering the fallback
	// first would fire its requests only to discard them once the placement lands.
	const is_resolved = computed(() => marketingSectionsStore.getSection(slug) !== null);

	// Answered by the backend, as opposed to still loading or failed.
	const is_loaded = computed(() => {
		const section = marketingSectionsStore.getSection(slug);

		return section !== null && !section.failed;
	});

	// A column is only taken away once the backend says the placement is empty.
	// Until then it stays, so the page does not reflow when the answer lands and
	// a failed request leaves the layout as it was.
	const show_column = computed(() => !is_loaded.value || has_content.value);

	const hasWidgetType = (type) => marketingSectionsStore.hasWidgetType(slug, type);

	return {
		has_content,
		is_resolved,
		is_loaded,
		show_column,
		hasWidgetType,
	};
}
